import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Plus, X } from 'lucide-react';
import { CHANNEL_OPTIONS, MACRO_CATEGORIES, TOKEN_HINTS } from '@/lib/crmMacros';

const EMPTY = {
  name: '',
  category: 'General',
  channel: 'email',
  subject: '',
  body: '',
  active: true,
  sort_order: 0,
};

const inputCls =
  'w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/60 transition-colors placeholder:text-white/20';

// Create or edit one canned response. Pass a macro to edit it, or nothing to
// start a new one.
export default function MacroFormModal({ macro, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(macro || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addToken = (token) => set('body', `${form.body || ''}${token}`);

  const save = async () => {
    if (!form.name.trim()) {
      setError('Give this response a title.');
      return;
    }
    if (!form.body.trim()) {
      setError('The response needs some body text.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || 'General',
        channel: form.channel || 'email',
        subject: form.subject || '',
        body: form.body,
        active: form.active !== false,
        sort_order: Number(form.sort_order) || 0,
      };
      if (macro?.id) {
        await base44.entities.CrmMacro.update(macro.id, payload);
      } else {
        await base44.entities.CrmMacro.create(payload);
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save this response.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl border border-white/10 bg-[#0d0d0f] max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#0d0d0f]">
          <p className="text-white text-xs font-bold tracking-widest">
            {macro?.id ? 'EDIT CANNED RESPONSE' : 'NEW CANNED RESPONSE'}
          </p>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="TITLE / LABEL">
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Parking & Arrival Directions"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="CATEGORY">
              <select
                className={inputCls}
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
              >
                {MACRO_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="TARGET CHANNEL">
              <select
                className={inputCls}
                value={form.channel}
                onChange={(e) => set('channel', e.target.value)}
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="SUBJECT LINE (USED WHEN STARTING A FRESH EMAIL)">
            <input
              className={inputCls}
              value={form.subject || ''}
              onChange={(e) => set('subject', e.target.value)}
              placeholder="e.g. Directions and arrival details for your visit"
            />
          </Field>

          <Field label="BODY">
            <textarea
              className={`${inputCls} resize-none leading-relaxed`}
              rows={12}
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Write the response, using tokens for anything that changes per customer."
            />
          </Field>

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">
              INSERT A TOKEN
            </p>
            <div className="flex flex-wrap gap-2">
              {TOKEN_HINTS.map((token) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => addToken(token)}
                  className="inline-flex items-center gap-1 border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-[10px] font-mono px-2.5 py-1.5 transition-colors"
                >
                  <Plus className="w-3 h-3" /> {token}
                </button>
              ))}
            </div>
            <p className="text-white/25 text-[10px] mt-2 leading-relaxed">
              Tokens are filled in when the response is inserted. Anything we can't resolve is left
              visible so you can see it needs attention.
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => set('active', e.target.checked)}
              className="w-4 h-4 accent-[#dd1f26]"
            />
            <span className="text-white/70 text-xs">
              Active — offered in the reply boxes
            </span>
          </label>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="border border-white/10 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-4 py-2.5 transition-colors"
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-5 py-2.5 transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'SAVING…' : 'SAVE RESPONSE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">{label}</p>
      {children}
    </div>
  );
}